import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { Schools } from './pages/education/Schools';
import { Admissions } from './pages/education/Admissions';
import { Exams } from './pages/education/Exams';
import { Scholarships } from './pages/education/Scholarships';
import { Teachers } from './pages/education/Teachers';
import { Licensing } from './pages/education/Licensing';
import { Recognition } from './pages/tertiary/Recognition';
import { ProfessionalRegister } from './pages/tertiary/ProfessionalRegister';
import { Apprenticeships } from './pages/tertiary/Apprenticeships';
import { Accreditation } from './pages/tertiary/Accreditation';
import { Shell } from './ui/Shell';

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Two workspaces, not one-per-role — land each persona somewhere useful
  // for their primary duty within their workspace (same idiom as
  // health-portal's per-workspace landing-by-role).
  const roles = persona.roles;
  const educationLanding = roles.includes('ADMISSIONS_OFFICER')
    ? '/education/admissions'
    : roles.includes('EXAMS_OFFICER')
      ? '/education/exams'
      : roles.includes('ANHRD_BOARD')
        ? '/education/scholarships'
        : roles.includes('SCHOOL_ADMIN')
          ? '/education/schools'
          : '/education/schools';
  const tertiaryLanding = roles.includes('ACCREDITATION_PANEL')
    ? '/tertiary-tvet/accreditation'
    : roles.some((r) => r.startsWith('REGISTRY_BOARD:'))
      ? '/tertiary-tvet/professional-register'
      : '/tertiary-tvet/recognition';
  const landing = persona.workspace === 'tertiary-tvet' ? tertiaryLanding : educationLanding;

  return (
    <Routes>
      <Route path="/" element={<Navigate to={landing} replace />} />

      <Route
        path="/education"
        element={
          <Shell
            title="Education Services"
            items={[
              { to: '/education/schools', label: 'Schools & Students', icon: '⌂' },
              { to: '/education/admissions', label: 'Admissions', icon: '▤' },
              { to: '/education/exams', label: 'Exams', icon: '✓' },
              { to: '/education/scholarships', label: 'Scholarships (ANHRD)', icon: '★' },
              { to: '/education/teachers', label: 'Teacher Register', icon: '◔' },
              { to: '/education/licensing', label: 'Private School Licensing', icon: '⚑' },
            ]}
          />
        }
      >
        <Route index element={<Navigate to={educationLanding} replace />} />
        <Route path="schools" element={<Schools />} />
        <Route path="admissions" element={<Admissions />} />
        <Route path="exams" element={<Exams />} />
        <Route path="scholarships" element={<Scholarships />} />
        <Route path="teachers" element={<Teachers />} />
        <Route path="licensing" element={<Licensing />} />
      </Route>

      <Route
        path="/tertiary-tvet"
        element={
          <Shell
            title="Tertiary, TVET & Qualifications (SQA)"
            items={[
              { to: '/tertiary-tvet/recognition', label: 'Qualification Recognition', icon: '⌗' },
              { to: '/tertiary-tvet/professional-register', label: 'Professional Register', icon: '☷' },
              { to: '/tertiary-tvet/apprenticeships', label: 'Apprenticeships', icon: '⛭' },
              { to: '/tertiary-tvet/accreditation', label: 'Institution Accreditation', icon: '◈' },
            ]}
          />
        }
      >
        <Route index element={<Navigate to={tertiaryLanding} replace />} />
        <Route path="recognition" element={<Recognition />} />
        <Route path="professional-register" element={<ProfessionalRegister />} />
        <Route path="apprenticeships" element={<Apprenticeships />} />
        <Route path="accreditation" element={<Accreditation />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
